import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import {
  buildStaffClaims,
  claimMapToRoleFlags,
  contactTypeToRoleFlags,
  detectAssignedRoleId,
  FEATURE_LABELS,
  FeatureId,
  FeatureLevel,
  FeatureScopes,
  getRoleTemplate,
  levelOptionsFor,
  resolveFeatureScopes,
  roleFlagsToContactType,
  scopesFromRecord,
  syncVehicleSplitScopes,
  userClaimsToMap,
  visibleFeatureIds,
} from "../../auth/featureScopes";
import useCustomerRoles from "../../data/useCustomerRoles";
import Modal from "../../layouts/Modal";
import { ContactType } from "../../models/Contact";
import useWilmaUsers from "../../data/useWilmaUsers";
import { TextInput } from "../shared/Inputs/TextInput";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { IWilmaUser } from "./../../models/auth/WilmaUser";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { useAuth } from "../../contexts/AuthContext";
import StaffPasswordModal from "./StaffPasswordModal";
import useCustomerDetails from "../../data/useCustomerDetails";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import LoadingWheel from "../shared/LoadingWheel";

type FormValues = {
  forename: string;
  surname: string;
  email: string;
  username: string;
  dismantleTarget: string;
  uname: string;
};

interface StaffModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  staff: IWilmaUser | undefined;
}

const CUSTOM_ROLE = "custom";

export default function StaffModal({ open, setOpen, staff }: StaffModalProps) {
  const { customerId } = useAuth();
  const { saveHandlers } = useDefaultCRUDHandlers("Staff");
  const { update, create, remove } = useWilmaUsers();
  const { customer } = useCustomerDetails();
  const { roles } = useCustomerRoles();
  const customerRoles = roles.data?.data ?? [];
  const isTme = CheckFeatureFlag("TME");
  const featureIds = visibleFeatureIds(isTme);
  const [prefix, setPrefix] = useState<string>("");
  const [selectedRoleId, setSelectedRoleId] = useState<string>(CUSTOM_ROLE);
  const [contactType, setContactType] = useState<ContactType>(ContactType.Staff);
  const [featureScopes, setFeatureScopes] = useState<FeatureScopes>(
    () => getRoleTemplate("staff")!.scopes
  );
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [updatePasswordOpen, setUpdatePasswordOpen] = useState<boolean>(false);
  const { register, handleSubmit, reset, watch, setValue } = useForm<FormValues>();

  const applyCustomerRole = (roleId: string) => {
    if (roleId === CUSTOM_ROLE) {
      setSelectedRoleId(CUSTOM_ROLE);
      return;
    }
    const role = customerRoles.find((r) => r.id === roleId);
    if (!role) return;
    setSelectedRoleId(roleId);
    setContactType(role.contactType as ContactType);
    setFeatureScopes(scopesFromRecord(role.featureScopes));
  };

  const updateScope = (feature: FeatureId, level: FeatureLevel) => {
    setFeatureScopes((prev) => ({ ...prev, [feature]: level }));
    setSelectedRoleId(CUSTOM_ROLE);
  };

  const onSubmit = (data: any) => {
    const flags = contactTypeToRoleFlags(contactType);
    // The landing page follows the role; a Custom assignment has none and falls back to
    // the automatic behaviour.
    const selectedRole = customerRoles.find((r) => r.id === selectedRoleId);
    const claims = buildStaffClaims(
      flags,
      syncVehicleSplitScopes(featureScopes, isTme),
      selectedRoleId,
      selectedRole?.defaultRoute,
      selectedRole?.topNavItems
    );

    const updatedStaff: any = {
      ...staff,
      ...data,
      dismantleTarget: data.dismantleTarget !== "" ? parseInt(data.dismantleTarget) : null,
      claims: claims,
      customerId,
      status: data.email !== "" ? 3 : 2, //Invited
      email: data.email !== "" ? data.email : null,
    };

    if (staff !== undefined) {
      update.mutate(updatedStaff, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    } else {
      create.mutate(updatedStaff, {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setOpen(false);
          reset({});
        },
        onError: saveHandlers.onError,
      });
    }
  };

  const handleDelete = () => {
    remove.mutate(staff?.id!, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
        setDeleteOpen(false);
        reset({});
      },
      onError: saveHandlers.onError,
    });
  };

  useEffect(() => {
    if (!customer.isLoading && customer.data) {
      setPrefix(customer.data.data.usernamePrefix || "");
    }
  }, [customer.isLoading, customer.data]);

  useEffect(() => {
    if (!open || roles.isLoading) return;

    if (staff !== undefined) {
      const claimMap = userClaimsToMap(staff.userClaims);
      const flags = claimMapToRoleFlags(claimMap);
      const type = roleFlagsToContactType(flags) ?? ContactType.Staff;
      const assignedId = detectAssignedRoleId(claimMap);
      const matchedRole = customerRoles.find((r) => r.id === assignedId);

      // Prefer the live role definition so staff pick up role edits; only fall
      // back to the user's stored claims when they are on a Custom assignment.
      if (matchedRole) {
        setSelectedRoleId(matchedRole.id);
        setContactType(matchedRole.contactType as ContactType);
        setFeatureScopes(scopesFromRecord(matchedRole.featureScopes));
      } else {
        setSelectedRoleId(CUSTOM_ROLE);
        setContactType(type);
        setFeatureScopes(resolveFeatureScopes(claimMap, flags));
      }
      reset({
        ...staff,
        dismantleTarget: staff.dismantleTarget ? String(staff.dismantleTarget) : "",
        uname: staff.username.replace(`${prefix}-`, ""),
      });
    } else {
      const defaultRole =
        customerRoles.find((r) => r.name.toLowerCase() === "staff") ??
        customerRoles[0];
      if (defaultRole) {
        setSelectedRoleId(defaultRole.id);
        setContactType(defaultRole.contactType as ContactType);
        setFeatureScopes(scopesFromRecord(defaultRole.featureScopes));
      } else {
        setSelectedRoleId(CUSTOM_ROLE);
        setContactType(ContactType.Staff);
        setFeatureScopes(getRoleTemplate("staff")!.scopes);
      }
      reset({
        forename: "",
        surname: "",
        email: "",
        username: "",
        dismantleTarget: "",
      });
    }
  }, [staff, open, reset, prefix, roles.isLoading, roles.data]);

  const uname = watch("uname");

  useEffect(() => {
    if (uname) {
      const formattedUname = uname.toLowerCase().replace(/[^a-z0-9]/g, "");
      if (formattedUname.includes("@")) {
        setValue("username", formattedUname);
        return;
      }

      setValue("username", `${prefix}-${formattedUname}`);
    } else setValue("username", "");
  }, [uname]);

  return (
    <>
      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        title={"Delete staff"}
        message={`Are you sure you want to delete this staff member?\n This action cannot be undone.`}
        confirmButtonText={"Delete"}
        onConfirm={handleDelete}
        isLoading={false}
      />
      <StaffPasswordModal open={updatePasswordOpen} setOpen={setUpdatePasswordOpen} staff={staff} />
      <Modal width="max-w-3xl" open={open} setOpen={setOpen}>
        <h1 className="text-xl">{`${staff ? "Edit" : "Add"} Staff`}</h1>
        <div className="mt-4">
          {roles.isLoading ? (
            <LoadingWheel />
          ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 border-t border-gray-200"
          >
            <div className="pt-4 grid grid-cols-1 gap-x-4 gap-y-4 sm:gap-y-6 sm:grid-cols-2">
              <TextInput
                label="First Name"
                registerName="forename"
                register={register}
              />
              <TextInput
                label="Last Name"
                registerName="surname"
                register={register}
              />

              <div className="flex items-end gap-2">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-[3px]">
                    Prefix
                  </label>
                  <div className="p-[6px] px-3 mt-[5px] bg-gray-300 text-gray-500 rounded border-b border-gray-400 shadow">
                    {prefix}
                  </div>
                </div>
                <span className="text-sm font-medium pb-2">
                  -
                </span>
                <TextInput
                  label="Username"
                  registerName="uname"
                  register={register}
                />
              </div>

              <TextInput
                label="Your Username"
                disabled={true}
                registerName="username"
                register={register}
              />

              <TextInput
                label="Email"
                disabled={staff !== undefined}
                registerName="email"
                register={register}
              />

              <div className="sm:col-span-1">
                <label className="text-sm font-medium text-gray-500">
                  Role
                </label>
                <select
                  aria-label="Role"
                  value={selectedRoleId}
                  onChange={(e) => applyCustomerRole(e.target.value)}
                  className="w-full mt-1 text-sm text-gray-900 h-[40px]"
                >
                  {customerRoles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                  <option value={CUSTOM_ROLE}>Custom</option>
                </select>
                {customerRoles.length === 0 && (
                  <p className="mt-1 text-xs text-amber-600">
                    No roles yet — create some under Admin Settings → Roles.
                  </p>
                )}
              </div>

              <TextInput
                label="Password"
                disabled={staff !== undefined}
                registerName="password"
                register={register}
                type="password"
              />
              <TextInput
                label="Dismantle Target"
                registerName="dismantleTarget"
                register={register}
              />

              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-gray-500">
                  Feature access
                </label>
                <p className="mt-1 text-xs text-gray-400">
                  Editing a level switches the role to Custom. Prefer changing the shared role under Admin → Roles so it updates everyone assigned.
                </p>
                <div className="mt-3 overflow-hidden rounded border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Feature
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">
                          Access
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {featureIds.map((feature) => (
                        <tr key={feature}>
                          <td className="px-3 py-2 text-gray-900">
                            {FEATURE_LABELS[feature]}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              aria-label={`${FEATURE_LABELS[feature]} access`}
                              value={featureScopes[feature]}
                              onChange={(e) =>
                                updateScope(
                                  feature,
                                  Number(e.target.value) as FeatureLevel
                                )
                              }
                              className="w-full max-w-[10rem] text-sm text-gray-900 h-[36px]"
                            >
                              {levelOptionsFor(feature).map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <ButtonInput
                label={"Save"}
                isSubmit={true}
                onClick={undefined}
                classes={undefined}
              />
              {staff !== undefined && (
                <ButtonInput
                  label={"Delete"}
                  isSubmit={false}
                  onClick={() => setDeleteOpen(true)}
                  classes={"!bg-red-500 hover:!bg-red-600"}
                />
              )}
              {staff !== undefined && (
                <div className="mt-2 text-sm text-gray-500 sm:col-span-2 text-center hover:text-gray-700 cursor-pointer" onClick={() => setUpdatePasswordOpen(true)} >
                  Change Password
                </div>
              )}
            </div>
          </form>
          )}
        </div>
      </Modal>
    </>
  );
}
